import { Button, Input, Segmented, Table, Tabs, Tag, Tooltip } from "antd";
import {
	ClockCircleOutlined,
	DollarOutlined,
	EditOutlined,
	EyeOutlined,
	ReloadOutlined,
} from "@ant-design/icons";
import "bootstrap-icons/font/bootstrap-icons.css";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	countLoan,
	loadAllLoan,
	loadLoanByStatus,
} from "../../redux/rtk/features/loan/loanSlice";
import { loadAllStaff } from "../../redux/rtk/features/user/userSlice";
import ColVisibilityDropdown from "../Shared/ColVisibilityDropdown";
import { GreenLinkBtn } from "../UI/AllLinkBtn";
import BtnAllSvg from "../UI/Button/btnAllSvg";
const { TabPane } = Tabs;

// Loan Requests Table Component
function LoanRequestsTable({ list, total, status, onStatusChange, onRefresh }) {
	const dispatch = useDispatch();
	const [columnsToShow, setColumnsToShow] = useState([]);
	const staffList = useSelector((state) => state.users?.list || []);
	const [searchEmployee, setSearchEmployee] = useState("");

	const mapEmployeeName = (employeeId) => {
		const employee = staffList.find((e) => e.id === employeeId);
		if (employee) {
			return (
				employee.EmployeeName ||
				`${employee.firstName || ""} ${employee.lastName || ""}`.trim() ||
				employee.UserName
			);
		}
		return employeeId;
	};

	const columns = [
		{
			title: "Employee",
			dataIndex: "employeeName",
			key: "employeeName",
		},
		{
			title: "Transaction Type",
			key: "requestType",
			dataIndex: "requestType",
			render: (type) => (
				<Tag color="blue">{type === "Loan" ? "Loan" : "Advance"}</Tag>
			),
		},
		{
			title: "Amount (₹)",
			dataIndex: "loanAmount",
			key: "loanAmount",
			render: (amount) => {
				if (!amount) return "-";
				return `₹ ${Number(amount).toLocaleString("en-IN")}`;
			},
		},
		{
			title: "Purpose",
			dataIndex: "purposeCategory",
			key: "purposeCategory",
			render: (purpose) => (purpose ? purpose.substring(0, 20) + (purpose.length > 20 ? "..." : "") : "-"),
		},
		{
			title: "Returning Months",
			dataIndex: "returningMonths",
			key: "returningMonths",
			render: (months) => (months ? `${months} months` : "-"),
		},
		{
			title: "Requested Date",
			dataIndex: "requestedDate",
			key: "requestedDate",
			render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
		},
		{
			title: "Status",
			dataIndex: "approvalStatus",
			key: "approvalStatus",
			render: (approvalStatus) => {
				if (approvalStatus === "APPROVED") {
					return <Tag color="green">Approved</Tag>;
				} else if (approvalStatus === "REJECTED") {
					return <Tag color="red">Rejected</Tag>;
				} else {
					return <Tag color="orange">Pending</Tag>;
				}
			},
		},
		{
			title: "Actions",
			key: "action",
			render: ({ id }) => (
				<Button
					type="default"
					icon={<EyeOutlined />}
					onClick={() => {
						// Navigate to loan detail page
						window.location.href = `/admin/loan/${id}`;
					}}
				>
					View
				</Button>
			),
		},
	];

	useEffect(() => {
		setColumnsToShow(columns);
	}, []);

	const columnsToShowHandler = (val) => {
		setColumnsToShow(val);
	};

	const addKeys = (arr) =>
		(Array.isArray(arr) ? arr : []).map((i, index) => ({
			...i,
			key: i.id || i.LoanId || index,
			serial: index + 1,
			employeeName: mapEmployeeName(i.employeeId || i.EmployeeId),
		}));

	const filteredData = addKeys(list).filter((item) => {
		const employeeName = (item.employeeName || "").toLowerCase();
		return employeeName.includes(searchEmployee.toLowerCase());
	});

	return (
		<div>
			<div className="flex my-2 justify-between mb-4">
				<div className="w-50">
					<h4 className="text-2xl mb-2">Loan Requests</h4>
				</div>
				<div className="flex justify-end mr-4 gap-2">
					<Button
						icon={<ReloadOutlined />}
						onClick={onRefresh}
						type="default"
					>
						Refresh
					</Button>
					<GreenLinkBtn>
						<button onClick={onRefresh}>
							<BtnAllSvg size={15} title={"ALL"} />
						</button>
					</GreenLinkBtn>
					<Segmented
						className="text-center rounded"
						size="middle"
						value={status}
						onChange={onStatusChange}
						options={[
							{
								label: <span>Pending</span>,
								value: "PENDING",
							},
							{
								label: <span>Approved</span>,
								value: "APPROVED",
							},
							{
								label: <span>Rejected</span>,
								value: "REJECTED",
							},
						]}
					/>
				</div>
			</div>

			<div
				style={{
					marginBottom: "20px",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: "10px",
				}}
			>
				<ColVisibilityDropdown
					options={columns}
					columns={columns}
					columnsToShowHandler={columnsToShowHandler}
				/>

				<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
					<Input
						placeholder="Search by Employee Name"
						value={searchEmployee}
						onChange={(e) => setSearchEmployee(e.target.value)}
						style={{ width: 200 }}
					/>
				</div>
			</div>

			<Table
				className="text-center"
				scroll={{ x: true }}
				loading={!list}
				pagination={{
					defaultPageSize: 20,
					pageSizeOptions: [10, 20, 50, 100, 200],
					showSizeChanger: true,
					total: total || 0,
					onChange: (page, limit) => {
						dispatch(
							loadLoanByStatus({ page, limit, approvalStatus: status })
						);
					},
				}}
				columns={columnsToShow}
				dataSource={filteredData}
			/>
		</div>
	);
}

// Active Loans Table Component
function ActiveLoansTable({ list, total, onRefresh }) {
	const dispatch = useDispatch();
	const [columnsToShow, setColumnsToShow] = useState([]);
	const staffList = useSelector((state) => state.users?.list || []);
	const [searchEmployee, setSearchEmployee] = useState("");

	const mapEmployeeName = (employeeId) => {
		const employee = staffList.find((e) => e.id === employeeId);
		if (employee) {
			return (
				employee.EmployeeName ||
				`${employee.firstName || ""} ${employee.lastName || ""}`.trim() ||
				employee.UserName
			);
		}
		return employeeId;
	};

	const columns = [
		{
			title: "Employee",
			dataIndex: "employeeName",
			key: "employeeName",
		},
		{
			title: "Loan ID",
			dataIndex: "loanId",
			key: "loanId",
			render: (id) => id || "-",
		},
		{
			title: "Transaction Type",
			key: "requestType",
			dataIndex: "requestType",
			render: (type) => (
				<Tag color="blue">{type === "Loan" ? "Loan" : "Advance"}</Tag>
			),
		},
		{
			title: "Amount (₹)",
			dataIndex: "loanAmount",
			key: "loanAmount",
			render: (amount) => {
				if (!amount) return "-";
				return `₹ ${Number(amount).toLocaleString("en-IN")}`;
			},
		},
		{
			title: "Returning Months",
			dataIndex: "returningMonths",
			key: "returningMonths",
			render: (months) => (months ? `${months} months` : "-"),
		},
		{
			title: "Start Date",
			dataIndex: "startDate",
			key: "startDate",
			render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
		},
		{
			title: "Balance (₹)",
			dataIndex: "balance",
			key: "balance",
			render: (balance) => {
				if (balance === undefined || balance === null) return "-";
				return `₹ ${Number(balance).toLocaleString("en-IN")}`;
			},
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: () => <Tag color="green">Active</Tag>,
		},
		{
			title: "Actions",
			key: "action",
			render: ({ id }) => (
				<div style={{ display: "flex", gap: "8px" }}>
					<Tooltip title="Record EMI">
						<Button
							type="primary"
							icon={<DollarOutlined />}
							size="small"
							onClick={() => {
								// Handle Record EMI
								console.log("Record EMI for loan:", id);
							}}
						>
							Record EMI
						</Button>
					</Tooltip>
					<Tooltip title="EMI History">
						<Button
							type="default"
							icon={<ClockCircleOutlined />}
							size="small"
							onClick={() => {
								// Handle EMI History
								console.log("EMI History for loan:", id);
							}}
						>
							EMI History
						</Button>
					</Tooltip>
					<Tooltip title="Edit">
						<Button
							type="default"
							icon={<EditOutlined />}
							size="small"
							onClick={() => {
								// Handle Edit
								console.log("Edit loan:", id);
							}}
						>
							Edit
						</Button>
					</Tooltip>
				</div>
			),
		},
	];

	useEffect(() => {
		setColumnsToShow(columns);
	}, []);

	const columnsToShowHandler = (val) => {
		setColumnsToShow(val);
	};

	const addKeys = (arr) =>
		(Array.isArray(arr) ? arr : []).map((i, index) => ({
			...i,
			key: i.id || i.LoanId || index,
			serial: index + 1,
			employeeName: mapEmployeeName(i.employeeId || i.EmployeeId),
			loanId: i.id || i.LoanId || i.loanId,
			balance: i.balance || i.Balance || i.loanAmount || i.loanAmount,
		}));

	const filteredData = addKeys(list).filter((item) => {
		const employeeName = (item.employeeName || "").toLowerCase();
		return employeeName.includes(searchEmployee.toLowerCase());
	});

	return (
		<div>
			<div className="flex my-2 justify-between mb-4">
				<div className="w-50">
					<h4 className="text-2xl mb-2">Active Loans</h4>
				</div>
				<div className="flex justify-end mr-4">
					<Button
						icon={<ReloadOutlined />}
						onClick={onRefresh}
						type="default"
					>
						Refresh
					</Button>
				</div>
			</div>

			<div
				style={{
					marginBottom: "20px",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: "10px",
				}}
			>
				<ColVisibilityDropdown
					options={columns}
					columns={columns}
					columnsToShowHandler={columnsToShowHandler}
				/>

				<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
					<Input
						placeholder="Search by Employee Name"
						value={searchEmployee}
						onChange={(e) => setSearchEmployee(e.target.value)}
						style={{ width: 200 }}
					/>
				</div>
			</div>

			<Table
				className="text-center"
				scroll={{ x: true }}
				loading={!list}
				pagination={{
					defaultPageSize: 20,
					pageSizeOptions: [10, 20, 50, 100, 200],
					showSizeChanger: true,
					total: total || 0,
					onChange: (page, limit) => {
						dispatch(loadAllLoan({ page, limit }));
					},
				}}
				columns={columnsToShow}
				dataSource={filteredData}
			/>
		</div>
	);
}

const LoanStatus = (props) => {
	const dispatch = useDispatch();
	const [activeTab, setActiveTab] = useState("requests");
	const [status, setStatus] = useState("PENDING");
	const list = useSelector((state) => state.loan?.list || []);
	const total = useSelector((state) => state.loan?.total || 0);

	// Filter active loans (approved loans)
	const activeLoans = list.filter(
		(loan) => loan.approvalStatus === "APPROVED" || loan.status === "ACTIVE"
	);

	useEffect(() => {
		dispatch(loadAllLoan());
		dispatch(countLoan());
		dispatch(loadAllStaff({ status: true }));
	}, [dispatch]);

	const handleStatusChange = (value) => {
		setStatus(value);
		dispatch(loadLoanByStatus({ page: 1, limit: 10, approvalStatus: value }));
	};

	const handleRefresh = () => {
		if (activeTab === "requests") {
			dispatch(loadLoanByStatus({ page: 1, limit: 10, approvalStatus: status }));
		} else {
			dispatch(loadAllLoan());
		}
	};

	return (
		<div>
			<div className="card card-custom">
				<div className="card-body">
					<Tabs
						activeKey={activeTab}
						onChange={setActiveTab}
						type="card"
					>
						<TabPane tab="Loan Requests" key="requests">
							<LoanRequestsTable
								list={list}
								total={total}
								status={status}
								onStatusChange={handleStatusChange}
								onRefresh={handleRefresh}
							/>
						</TabPane>
						<TabPane tab="Active Loans" key="active">
							<ActiveLoansTable
								list={activeLoans}
								total={activeLoans.length}
								onRefresh={handleRefresh}
							/>
						</TabPane>
					</Tabs>
				</div>
			</div>
		</div>
	);
};

export default LoanStatus;

